// Google Tag Tracking utilities for conversion tracking

// Type declarations are handled elsewhere, we just use them here

// Check if tracking is allowed based on consent
const isTrackingAllowed = (type: 'analytics' | 'marketing'): boolean => {
  try {
    const consent = localStorage.getItem('cookieConsent');
    if (!consent) return false;
    const preferences = JSON.parse(consent);
    return type === 'analytics' ? preferences.analytics : preferences.marketing;
  } catch {
    return false;
  }
};

// Initialize gtag if not already present
const ensureGtag = () => {
  if (!window.gtag) {
    window.dataLayer = window.dataLayer || [];
    window.gtag = function() {
      window.dataLayer!.push(arguments);
    };
  }
};

// Track page view events for micro-conversions
export const trackPageView = (pageName: string, pageCategory?: string) => {
  if (!isTrackingAllowed('analytics')) return;
  
  ensureGtag();
  if (window.gtag) {
    window.gtag('event', 'page_view', {
      page_title: pageName,
      page_location: window.location.href,
      page_path: window.location.pathname,
      custom_page_category: pageCategory
    });
  }
};

// Track investor info page view (micro-conversion)
export const trackInvestorPageView = () => {
  if (!isTrackingAllowed('analytics')) return;
  
  ensureGtag();
  if (window.gtag) {
    // GA4 Event for investor page
    window.gtag('event', 'page_view_investor_info', {
      page_location: window.location.href,
      page_path: window.location.pathname,
      page_referrer: document.referrer,
      value: 0.5,
      currency: 'EUR'
    });
    
    // Google Ads Remarketing Event
    if (isTrackingAllowed('marketing')) {
      window.gtag('event', 'view_item', {
        send_to: 'AW-17412433587',
        value: 1,
        items: [{
          id: 'kapitalanlage',
          name: 'Kapitalanlage Information',
          category: 'investor_info',
          quantity: 1,
          price: 1
        }]
      });
    }
  }
};

// Track phone number clicks (micro-conversion)
export const trackPhoneClick = (phoneNumber: string) => {
  if (!isTrackingAllowed('analytics')) return;
  
  ensureGtag();
  if (window.gtag) {
    window.gtag('event', 'phone_click', {
      phone_number: phoneNumber,
      page_location: window.location.href
    });
  }
};

// Track email clicks
export const trackEmailClick = (email: string) => {
  if (!isTrackingAllowed('analytics')) return;
  
  ensureGtag();
  if (window.gtag) {
    window.gtag('event', 'email_click', {
      email_address: email,
      page_location: window.location.href
    });
  }
};

// Track expose/leitfaden view (when someone lands on the download page)
export const trackExposeView = () => {
  if (!isTrackingAllowed('analytics')) return;
  
  ensureGtag();
  if (window.gtag) {
    window.gtag('event', 'expose_view', {
      page_location: window.location.href,
      page_path: window.location.pathname
    });
  }
};

// Track expose/leitfaden download (main conversion)
export const trackExposeDownload = (documentType: 'leitfaden' | 'checkliste' | 'expose' = 'leitfaden') => {
  if (!isTrackingAllowed('marketing')) return;
  
  ensureGtag();
  if (window.gtag) {
    // GA4 Event for conversion tracking
    window.gtag('event', 'expose_download', {
      document_type: documentType,
      page_location: window.location.href,
      value: 1.0,
      currency: 'EUR'
    });
    
    // Google Ads Conversion Tracking
    window.gtag('event', 'conversion', {
      'send_to': 'AW-17412433587/YPgOCMODmfsaELPN8u5A',
      'value': 1.0,
      'currency': 'EUR',
      'transaction_id': Date.now().toString()
    });
  }
};

// Track form submission start
export const trackFormStart = (formName: string) => {
  if (!isTrackingAllowed('analytics')) return;
  
  ensureGtag();
  if (window.gtag) {
    window.gtag('event', 'form_start', {
      form_name: formName,
      page_location: window.location.href
    });
  }
};

// Track form submission (lead generation)
export const trackFormSubmit = (formName: string, formData?: any) => {
  if (!isTrackingAllowed('marketing')) return;
  
  ensureGtag();
  if (window.gtag) {
    // GA4 Event
    window.gtag('event', 'generate_lead', {
      form_name: formName,
      page_location: window.location.href,
      value: 1.0,
      currency: 'EUR'
    });
    
    // Google Ads Conversion for lead forms
    if (formName === 'lead_form' || formName === 'contact_form' || formName === 'kapitalanlage_kaufen') {
      window.gtag('event', 'conversion', {
        'send_to': 'AW-17412433587/YPgOCMODmfsaELPN8u5A',
        'value': 1.0,
        'currency': 'EUR',
        'transaction_id': Date.now().toString()
      });
    }
  }
};

// Track bookings (Calendly / YouCanBookMe clicks)
export const trackBookingClick = (source: string) => {
  const analyticsAllowed = isTrackingAllowed('analytics');
  const marketingAllowed = isTrackingAllowed('marketing');
  if (!analyticsAllowed && !marketingAllowed) return;

  ensureGtag();
  if (analyticsAllowed && window.gtag) {
    window.gtag('event', 'booking_click', {
      source,
      page_location: window.location.href
    });
  }

  if (marketingAllowed && window.gtag) {
    window.gtag('event', 'conversion', {
      send_to: 'AW-17412433587/YPgOCMODmfsaELPN8u5A',
      value: 1.0,
      currency: 'EUR',
      transaction_id: Date.now().toString()
    });
  }

  if (marketingAllowed) {
    try {
      const consent = localStorage.getItem('cookieConsent');
      if (consent) {
        const preferences = JSON.parse(consent);
        if (preferences.marketing && (window as any).fbq) {
          (window as any).fbq('track', 'Schedule', { content_name: source });
        }
      }
    } catch {
      // ignore
    }
  }
};

// Track scroll depth for engagement
export const trackScrollDepth = (percentage: number) => {
  if (!isTrackingAllowed('analytics')) return;
  
  ensureGtag();
  if (window.gtag) {
    window.gtag('event', 'scroll', {
      percent_scrolled: percentage,
      page_location: window.location.href
    });
  }
};

// Track time on page (for engagement tracking)
export const trackTimeOnPage = (seconds: number) => {
  if (!isTrackingAllowed('analytics')) return;
  
  ensureGtag();
  if (window.gtag) {
    window.gtag('event', 'time_on_page', {
      engagement_time_msec: seconds * 1000,
      page_location: window.location.href
    });
  }
};

// Enhanced Ecommerce: Track property/listing views
export const trackPropertyView = (propertyId: string, propertyName: string, price?: number) => {
  if (!isTrackingAllowed('analytics')) return;
  
  ensureGtag();
  if (window.gtag) {
    window.gtag('event', 'view_item', {
      currency: 'EUR',
      value: price,
      items: [{
        item_id: propertyId,
        item_name: propertyName,
        price: price,
        item_category: 'immobilie'
      }]
    });
  }
};

// Track CTA button clicks
export const trackCTAClick = (ctaName: string, ctaLocation: string) => {
  if (!isTrackingAllowed('analytics')) return;
  
  ensureGtag();
  if (window.gtag) {
    window.gtag('event', 'cta_click', {
      cta_name: ctaName,
      cta_location: ctaLocation,
      page_location: window.location.href
    });
  }
};
